import { Component, inject, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { devLog, devWarn, devError } from '@app/core/utils/dev-logger';
import { RouterOutlet } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Subscription } from 'rxjs';
import { PrimeNG } from 'primeng/config';
import { primeNgTranslationFor } from '@app/core/i18n/primeng-translations';
import { ThemeService } from '@app/core/services/theme.service';
import { OfflineIndicatorComponent } from '@app/shared/components/molecules/offline-indicator/offline-indicator.component';
import { CacheManagerService } from '@app/core/cache';
import { GleapService } from '@app/core/services/gleap.service';
import { environment } from '@environ/environment';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastModule, ConfirmDialogModule, OfflineIndicatorComponent, TranslatePipe],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnDestroy {
  protected title = 'Intelligence by Idiogram';
  private langChangeSubscription?: Subscription;
  private document = inject(DOCUMENT);

  // Theme service - initializes dark mode automatically
  private themeService = inject(ThemeService);
  private cacheManager = inject(CacheManagerService);
  private primeng = inject(PrimeNG);
  
  constructor(private translate: TranslateService) {
    inject(GleapService);
    // Dark mode is now handled by ThemeService
    // It will:
    // 1. Detect OS preference on init
    // 2. Apply stored user preference if exists
    // 3. Listen to OS changes
    // 4. Keep PrimeNG and TailwindCSS in sync

    // Initialize translations
    this.initializeTranslations();

    // Expose cache manager to window for testing (dev only)
    if (!environment.production) {
      (window as any).cacheManager = this.cacheManager;
      devLog('🔧 CacheManager exposed to window.cacheManager for testing');
    }
  }

  private initializeTranslations(): void {
    // Get browser language
    const browserLang = navigator.language;
    const defaultLang = browserLang.startsWith('es') ? 'es' : 'en';

    // Set default language
    this.translate.setDefaultLang(defaultLang);

    // Use the language (this will load the translation files)
    this.translate.use(defaultLang).subscribe({
      next: (translations) => {
        devLog(`Translations loaded successfully for ${defaultLang}`, translations);
        this.document.documentElement.lang = defaultLang;
      },
      error: (error) => {
        devError('Error loading translations:', error);
        // Fallback to English if there's an error
        this.translate.use('en').subscribe({
          next: () => {
            devLog('Fallback to English translations');
            this.document.documentElement.lang = 'en';
          },
          error: (fallbackError) => {
            devError('Error loading fallback translations:', fallbackError);
          }
        });
      }
    });

    // Keep PrimeNG component-internal translations in sync with the initial language
    this.primeng.setTranslation(primeNgTranslationFor(defaultLang));

    // Update lang attribute + PrimeNG translations on language change
    this.langChangeSubscription = this.translate.onLangChange.subscribe(event => {
      this.document.documentElement.lang = event.lang;
      this.primeng.setTranslation(primeNgTranslationFor(event.lang));
      devLog(`Language changed to: ${event.lang}`);
    });
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
  }
}
